import type { ActionType } from "typesafe-actions";
import { createReducer } from "typesafe-actions";

import type {
    ExcludeAccountFields,
    ExcludeStatusFields,
    HubspotMappingField,
} from "app/pages/sales-intelligence/common-components/export-table/types";
import { INTEGRATION_STATUS } from "app/pages/sales-intelligence/pages/management/constants/constants";
import type { FetchError } from "app/pages/sales-intelligence/types";

import type { CrmExportLimit, CrmStateInfo, IntegrationStatus } from "../../components/types";
import { CrmFeedLevel, CRMListObjectsType } from "../../types";
import { managementActions } from "../action-creators";

export type DynamicsIntegrationState = {
    crmConnectionStatus: IntegrationStatus;
    crmStateInfo: CrmStateInfo;
    crmExportLimit: CrmExportLimit | null;
    companyAsLeadExportClaimEnabled: boolean;
    predefinedOnCreateUpdateClaimEnabled: boolean;
    isCurrentUserVerifiedByCrm: boolean;
    isLoading: boolean;
    toggleLoading: {
        isContactsLoading: boolean;
        isAccountsLoading: boolean;
        isLeadsLoading: boolean;
    };
    excludeStatusFields: ExcludeStatusFields;
    contactFields: string[];
    isLoadingMappingFields: boolean;
    mappingFields: HubspotMappingField[];
    mappingFieldsError: FetchError | null;
    isLoadingExcludeStatusFields: boolean;
    isExclusionOn: boolean | null;
    exclusionError: string;
    isExclusionLoading: boolean;
    error: string;
    excludeAccountFields: ExcludeAccountFields;
    isLoadingExcludeAccountFields: boolean;
    isUpdatingFilterFields: boolean;
    isUpdatingStatusField: boolean;
};

const DYNAMICS_INTEGRATION_STATE: DynamicsIntegrationState = {
    crmConnectionStatus: {
        ownerFirstName: "",
        ownerLastName: "",
        domainsCount: 0,
        syncStatus: INTEGRATION_STATUS.NotIntegrated,
        syncFailedReasonCode: 0,
        lastSynced: "",
        isExportEnabled: false,
        isContactExportEnabled: false,
        isLeadsExportEnabled: false,
        isCompanyAsLeadExportEnabled: false,
        isLeadsFilterEnabled: false,
        isCrmListEnabled: false,
        isCrmLabelsEnabled: false,
        isCrmFeedEnabled: false,
        crmFeedLevel: CrmFeedLevel.User,
        crmListObjectsType: CRMListObjectsType.AccountsAndLeads,
    },
    crmStateInfo: {
        accountExportFieldsCount: 0,
        contactExportFieldsCount: 0,
        leadExportFieldsCount: 0,
    },
    crmExportLimit: null,
    companyAsLeadExportClaimEnabled: false,
    predefinedOnCreateUpdateClaimEnabled: false,
    isCurrentUserVerifiedByCrm: false,
    toggleLoading: {
        isContactsLoading: false,
        isAccountsLoading: false,
        isLeadsLoading: false,
    },
    excludeStatusFields: [],
    contactFields: [],
    isLoadingMappingFields: false,
    mappingFields: [],
    mappingFieldsError: null,
    isLoadingExcludeStatusFields: false,
    isExclusionOn: null,
    isExclusionLoading: false,
    exclusionError: "",
    error: "",
    isLoading: false,
    isLoadingExcludeAccountFields: false,
    excludeAccountFields: [],
    isUpdatingFilterFields: false,
    isUpdatingStatusField: false,
};

export const dynamicsIntegrationReducer = createReducer<
    DynamicsIntegrationState,
    ActionType<typeof managementActions.dynamics>
>(DYNAMICS_INTEGRATION_STATE)
    .handleAction(managementActions.dynamics.getDynamicsStatusAsync.success, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: payload.crmConnectionStatus,
        crmStateInfo: payload.crmStateInfo,
        crmExportLimit: payload.crmExportLimit ?? null,
        isCurrentUserVerifiedByCrm: payload.isCurrentUserVerifiedByCrm,
        companyAsLeadExportClaimEnabled: payload.companyAsLeadExportClaimEnabled,
        predefinedOnCreateUpdateClaimEnabled: payload.predefinedOnCreateUpdateClaimEnabled,
        isLoading: false,
    }))
    .handleAction(managementActions.dynamics.getDynamicsStatusAsync.failure, (state, { payload }) => ({
        ...state,
        error: payload,
        isLoading: false,
    }))
    .handleAction(managementActions.dynamics.getDynamicsStatusAsync.request, (state, { payload }) => ({
        ...state,
        isLoading: payload,
    }))
    .handleAction(managementActions.dynamics.getExcludeStatusFieldsAsync.request, (state) => ({
        ...state,
        isLoadingExcludeStatusFields: true,
    }))
    .handleAction(managementActions.dynamics.getExcludeStatusFieldsAsync.success, (state, { payload }) => ({
        ...state,
        excludeStatusFields: payload,
        isLoadingExcludeStatusFields: false,
    }))
    .handleAction(managementActions.dynamics.getDynamicsContactsFieldsAsync.success, (state, { payload }) => ({
        ...state,
        contactFields: payload,
    }))
    .handleAction(managementActions.dynamics.getExcludeStatusFieldsAsync.failure, (state, { payload }) => ({
        ...state,
        isLoadingExcludeStatusFields: true,
        error: payload,
    }))
    .handleAction(managementActions.dynamics.getExcludeAccountFieldsAsync.request, (state) => ({
        ...state,
        isLoadingExcludeAccountFields: true,
    }))
    .handleAction(managementActions.dynamics.getExcludeAccountFieldsAsync.success, (state, { payload }) => ({
        ...state,
        excludeAccountFields: payload,
        isLoadingExcludeAccountFields: false,
    }))
    .handleAction(managementActions.dynamics.getExcludeAccountFieldsAsync.failure, (state, { payload }) => ({
        ...state,
        isLoadingExcludeAccountFields: false,
        error: payload,
    }))
    .handleAction(managementActions.dynamics.getMappingFieldsAsync.request, (state) => ({
        ...state,
        isLoadingMappingFields: true,
        mappingFieldsError: null,
    }))
    .handleAction(managementActions.dynamics.getMappingFieldsAsync.success, (state, { payload }) => ({
        ...state,
        mappingFields: payload,
        isLoadingMappingFields: false,
    }))
    .handleAction(managementActions.dynamics.getMappingFieldsAsync.failure, (state, { payload }) => ({
        ...state,
        mappingFieldsError: payload,
        isLoadingMappingFields: false,
    }))
    .handleAction(managementActions.dynamics.putFilterFieldsAsync.request, (state) => ({
        ...state,
        isUpdatingFilterFields: true,
    }))
    .handleAction(managementActions.dynamics.putFilterFieldsAsync.success, (state) => ({
        ...state,
        isUpdatingFilterFields: false,
    }))
    .handleAction(managementActions.dynamics.putFilterFieldsAsync.failure, (state) => ({
        ...state,
        isUpdatingFilterFields: false,
    }))
    .handleAction(managementActions.dynamics.putStatusFieldAsync.request, (state) => ({
        ...state,
        isUpdatingStatusField: true,
    }))
    .handleAction(managementActions.dynamics.putStatusFieldAsync.success, (state) => ({
        ...state,
        isUpdatingStatusField: false,
    }))
    .handleAction(managementActions.dynamics.putStatusFieldAsync.failure, (state) => ({
        ...state,
        isUpdatingStatusField: false,
    }))
    .handleAction(managementActions.dynamics.setSyncStatus, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: {
            ...state.crmConnectionStatus,
            syncStatus: payload,
        },
    }))
    .handleAction(managementActions.dynamics.disconnectDynamicsAsync.request, (state) => ({
        ...state,
        crmConnectionStatus: {
            ...state.crmConnectionStatus,
            syncStatus: INTEGRATION_STATUS.InProgress,
        },
        isLoading: true,
    }))
    .handleAction(managementActions.dynamics.setIsDynamicsAccountsExportEnabled, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: {
            ...state.crmConnectionStatus,
            isExportEnabled: payload,
        },
    }))
    .handleAction(managementActions.dynamics.setIsDynamicsContactsExportEnabled, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: {
            ...state.crmConnectionStatus,
            isContactExportEnabled: payload,
        },
    }))
    .handleAction(managementActions.dynamics.setIsDynamicsLeadsExportEnabled, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: {
            ...state.crmConnectionStatus,
            isLeadsExportEnabled: payload,
        },
    }))
    .handleAction(managementActions.dynamics.setIsDynamicsCompanyAsLeadsExportEnabled, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: {
            ...state.crmConnectionStatus,
            isCompanyAsLeadExportEnabled: payload,
        },
    }))
    .handleAction(managementActions.dynamics.disconnectDynamicsAsync.success, (state) => ({
        ...state,
        crmConnectionStatus: { ...DYNAMICS_INTEGRATION_STATE.crmConnectionStatus },
        isLoading: false,
    }))
    .handleAction(managementActions.dynamics.disconnectDynamicsAsync.failure, (state) => ({
        ...state,
        crmConnectionStatus: { ...DYNAMICS_INTEGRATION_STATE.crmConnectionStatus },
        isLoading: false,
    }))
    .handleAction(managementActions.dynamics.putIntegrationLeadFilterStatusAsync.request, (state) => ({
        ...state,
        isExclusionLoading: true,
    }))
    .handleAction(managementActions.dynamics.putIntegrationLeadFilterStatusAsync.success, (state, { payload }) => ({
        ...state,
        isExclusionOn: payload,
        isExclusionLoading: false,
    }))
    .handleAction(managementActions.dynamics.putIntegrationLeadFilterStatusAsync.failure, (state, { payload }) => ({
        ...state,
        exclusionError: payload,
        isExclusionLoading: false,
    }))
    .handleAction(managementActions.dynamics.getIntegrationLeadFilterStatusAsync.request, (state) => ({
        ...state,
        isExclusionLoading: true,
    }))
    .handleAction(managementActions.dynamics.getIntegrationLeadFilterStatusAsync.success, (state, { payload }) => ({
        ...state,
        isExclusionOn: payload,
        isExclusionLoading: false,
    }))
    .handleAction(managementActions.dynamics.getIntegrationLeadFilterStatusAsync.failure, (state, { payload }) => ({
        ...state,
        exclusionError: payload,
        isExclusionLoading: false,
    }))
    .handleAction(managementActions.dynamics.putContactsToggleAsync.success, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: payload.crmConnectionStatus,
        toggleLoading: { ...state.toggleLoading, isContactsLoading: false },
    }))
    .handleAction(managementActions.dynamics.putContactsToggleAsync.request, (state) => ({
        ...state,
        toggleLoading: { ...state.toggleLoading, isContactsLoading: true },
    }))
    .handleAction(managementActions.dynamics.putContactsToggleAsync.failure, (state) => ({
        ...state,
        toggleLoading: { ...state.toggleLoading, isContactsLoading: false },
    }))
    .handleAction(managementActions.dynamics.putAccountsStatusAsync.success, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: payload.crmConnectionStatus,
        toggleLoading: { ...state.toggleLoading, isAccountsLoading: false },
    }))
    .handleAction(managementActions.dynamics.putAccountsStatusAsync.request, (state) => ({
        ...state,
        toggleLoading: { ...state.toggleLoading, isAccountsLoading: true },
    }))
    .handleAction(managementActions.dynamics.putAccountsStatusAsync.failure, (state) => ({
        ...state,
        toggleLoading: { ...state.toggleLoading, isAccountsLoading: false },
    }))
    .handleAction(managementActions.dynamics.setIsDynamicsCrmLabelEnabled, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: { ...state.crmConnectionStatus, isCrmLabelsEnabled: payload },
    }))
    .handleAction(managementActions.dynamics.setIsDynamicsCrmListEnabled, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: { ...state.crmConnectionStatus, isCrmListEnabled: payload },
    }))
    .handleAction(managementActions.dynamics.setDynamicsCrmListObjectsType, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: { ...state.crmConnectionStatus, crmListObjectsType: payload },
    }))
    .handleAction(managementActions.dynamics.putLeadsToggleAsync.request, (state) => ({
        ...state,
        toggleLoading: { ...state.toggleLoading, isLeadsLoading: true },
    }))
    .handleAction(managementActions.dynamics.putLeadsToggleAsync.success, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: payload.crmConnectionStatus,
        toggleLoading: { ...state.toggleLoading, isLeadsLoading: false },
    }))
    .handleAction(managementActions.dynamics.putLeadsToggleAsync.failure, (state) => ({
        ...state,
        toggleLoading: { ...state.toggleLoading, isLeadsLoading: false },
    }));
